import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckBox,
  NumberBox,
  SelectBox,
  Tabs,
  TagBox,
  TextArea,
  TextBox,
  Validator,
  Autocomplete,
} from "devextreme-react";
import {
  AsyncRule,
  CustomRule,
  PatternRule,
  RangeRule,
  RequiredRule,
  StringLengthRule,
} from "devextreme-react/validator";
import { LookupsType } from "src/views/PlannedRunsCalendar/AddRunForm";
import {
  newRun,
  PlannedRunDetails,
  spatialZDefaults,
} from "src/views/PlannedRunsCalendar/constants";
import { useAuth0 } from "src/react-auth0-spa";
import { recipeBranchStore } from "src/views/PlannedRunsCalendar/AddRunSequencingContent";
import notify from "devextreme/ui/notify";

const labelStyle = {
  width: 300,
  marginTop: 3,
  paddingLeft: 20,
  color: "#909090",
};
const labelStyleAnalysis = { ...labelStyle, width: "300px" };
const itemStyle = {
  minWidth: 230,
  marginTop: 3,
  width: "100%",
  paddingRight: 20,
};
const rowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  alignSelf: "start",
  width: "100%",
};

const logLevels = ["Debug", "Fatal", "Info", "Warnings"];

const saveIntensitiesOptions = ["none", "some", "all"];

const runLengths = [
  { name: "Half-day", value: 6 },
  { name: "Day", value: 24 },
  { name: "Two-day", value: 48 },
];

const wellLayoutOptions = [
  { name: "Twelve Well - Circle", value: "TwelveWellCircle" },
  { name: "Twelve Well - Standard", value: "TwelveWellStandard" },
  { name: "Mega Well", value: "OneWell" },
  { name: "48 Well", value: "FortyEightWell" },
  // { name: "Two Well", value: "TwoWell"},
];

// Tissue runs use a Panos slide (08xxxxxxxx barcode) by default, or optionally a
// Mega Well (02xxxxxxxx) flow cell. Panos slides are not valid for other kit types.
// PanosSlide is hidden until the rest of the software suite supports it.
const defaultTissueWellLayout = "OneWell";
const tissueWellLayoutOptions = [
  // { name: "Panos Slide", value: "PanosSlide" },
  { name: "Mega Well", value: "OneWell" },
];

const spatialKitTypes = [
  "KitTypeCytoprofiling",
  "KitTypeAntibodyScreening",
  "KitTypeDirectInSampleSequencing",
  "KitTypeTissue",
  "KitTypeProteinScreening",
];

const proteinCartridgeOptions = [
  { Name: "AVITI24 Protein Cartridge", PartNumber: "820-00048" },
];

const spatialKitTypeMap = {
  KitTypeCytoprofiling: 1,
  KitTypeAntibodyScreening: 101,
  KitTypeDirectInSampleSequencing: 200,
  KitTypeTissue: 300,
  KitTypeProteinScreening: 400,
};

const NarrowWindowThreshold = 1050;

interface CartridgePartNumber {
  ID: string;
  Name: string;
  PartNumber: string;
  CompatiblePartNumbers: string[];
  Description: string;
  SpatialKitType: number[];
}

function AddRunSpatialContent({
  lookups,
  data,
  setData,
  setEditedData,
  setForceRefresh,
  nameIsReadOnly,
  cartridges,
}: {
  lookups: LookupsType;
  data: PlannedRunDetails;
  setData: React.Dispatch<React.SetStateAction<PlannedRunDetails>>;
  setEditedData: React.Dispatch<
    React.SetStateAction<Partial<PlannedRunDetails>>
  >;
  setForceRefresh: (val: boolean) => void;
  nameIsReadOnly: boolean;

  cartridges: CartridgePartNumber[];
}) {
  const { myFetch } = useAuth0();
  const [isNarrow, setIsNarrow] = useState(
    window.innerWidth < NarrowWindowThreshold,
  );
  const [tab, setTab] = useState("Cyto Parameters");
  const [enableEditingZSettings, setEnableEditingZSettings] = useState(false);

  const [spatialPanelParseMessage, setSpatialPanelParseMessage] = useState("");
  const [spacerManifestParseMessage, setSpacerManifestParseMessage] =
    useState("");
  const manifestValidatorRef = useRef<Validator>(null);
  const [filteredCartridges, setFilteredCartridges] =
    useState<CartridgePartNumber[]>(cartridges);

  // Post-run H&E and tile overlap only apply to tissue sections
  const isTissueKit = data.spatialKitType === "KitTypeTissue";

  useEffect(() => {
    setFilteredCartridges(
      cartridges.filter((v) =>
        v.SpatialKitType.includes(
          spatialKitTypeMap[data.spatialKitType || "KitTypeCytoprofiling"],
        ),
      ),
    );
  }, [data.spatialKitType, cartridges]);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrow(window.innerWidth < NarrowWindowThreshold);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (data.validateSpatialPanel === undefined) {
      setData((v) => ({ ...v, validateSpatialPanel: true }));
      setEditedData?.((v) => ({ ...v, validateSpatialPanel: true })); // if you track edited state
    }
  }, [data.validateSpatialPanel, setData, setEditedData]);

  // Re-validate manifest when panel changes (cross-validation depends on panel)
  useEffect(() => {
    if (data.spatialPanelJson && data.manifest) {
      manifestValidatorRef.current?.instance?.validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-validate when panel changes, not on every manifest edit
  }, [data.spatialPanelJson]);

  const validateManifest = useCallback(
    (params) =>
      new Promise<void>((resolve, reject) => {
        const pick = ({
          runName,
          manifest,
          wellLayout,
          spatialKitType,
          spatialPanelJson,
        }) => ({
          runName,
          manifest,
          wellLayout,
          spatialKitType,
          spatialPanelJson,
        });
        console.log("data", data);
        const tdata = pick(data);
        tdata.manifest = params.value;

        if (/\r\n/g.test(params.value)) {
          reject("Manifest cannot contain CRLF characters");
        }

        const payload = pick(tdata);
        console.log("manifest verify payload", payload);
        myFetch("/spatialManifest", {
          method: "POST",
          body: JSON.stringify({
            ...payload,
          }),
        })
          .then((response) => response.json())
          .then((resp) => {
            if (!resp.isValid) reject("Manifest: " + resp.message);
            else resolve();
          })
          .catch((error) => {
            reject("Cannot validate manifest: " + JSON.stringify(error));
          });
      }),
    [data, myFetch],
  );

  const validatePanel = useCallback(
    (params) =>
      new Promise<void>((resolve, reject) => {
        const pick = ({
          runName,
          spatialPanelType,
          spatialPanel,
          spatialKitType,
          cloudAnalysis,
          spatialCartridgePartNumber,
          spatialProteinPartNumber,
          validateSpatialPanel,
        }) => ({
          runName,
          spatialPanelType,
          spatialPanel,
          spatialKitType,
          cloudAnalysis,
          spatialCartridgePartNumber,
          spatialProteinPartNumber,
          validatePanel: validateSpatialPanel ?? true, // default to true if undefined
        });

        const payload = pick(data);
        payload.spatialPanel = params.value;
        console.log("panel verify payload", payload);

        if (!payload.spatialPanelType) {
          reject("Please select panel type to validate");
        }
        if (!payload.spatialPanel) {
          reject("Please enter spatial panel");
        }

        myFetch("/panelValidation", {
          method: "POST",
          body: JSON.stringify({
            ...payload,
          }),
        })
          .then((response) => response.json())
          .then((resp) => {
            if (!resp.isValid) {
              setSpatialPanelParseMessage("");
              reject("Spatial Panel: " + resp.message);
            } else {
              setData((v) => {
                return {
                  ...v,
                  spatialPanelJson: resp.panelJson,
                };
              });
              setSpatialPanelParseMessage(resp.message);
              resolve();
            }
          })
          .catch((error) => {
            reject("Cannot validate panel: " + JSON.stringify(error));
          });
      }),
    [data, myFetch, setData],
  );

  const validateSpacerManifest = useCallback(
    (params) =>
      new Promise<void>((resolve, reject) => {
        const pick = ({
          spacerManifestCsv,
          spatialPanelJson,
          spatialKitType,
        }) => ({
          spacerManifestCsv,
          spatialPanelJson,
          spatialKitType,
        });
        console.log("data", data);
        const tdata = pick(data);
        tdata.spacerManifestCsv = params.value;

        if (
          tdata.spatialKitType !== "KitTypeDirectInSampleSequencing" &&
          tdata.spatialKitType !== "KitTypeTissue"
        ) {
          resolve();
          return;
        }

        if (/\r\n/g.test(params.value)) {
          reject("Spacer Manifest cannot contain CRLF characters");
        }

        const payload = pick(tdata);
        console.log("spacer manifest verify payload", payload);
        // spacerManifestCsv is only required if panel has CellAssignment true
        if (!payload.spatialPanelJson) {
          console.log("reject spatial panel");
          reject("Please enter spatial panel");
        }
        myFetch("/processSpacerManifest", {
          method: "POST",
          body: JSON.stringify({
            ...payload,
          }),
        })
          .then((response) => response.json())
          .then((resp) => {
            if (!resp.isValid) {
              setSpacerManifestParseMessage("");
              reject("Spacer Manifest: " + resp.message);
            } else {
              if (resp.SpacerManifest !== data.spacerManifestJson) {
                setData((v) => {
                  return {
                    ...v,
                    spacerManifestJson: resp.SpacerManifest,
                  };
                });
                setEditedData((v) => {
                  return {
                    ...v,
                    spacerManifestJson: resp.SpacerManifest,
                  };
                });
                setSpacerManifestParseMessage(resp.message);
              }
              resolve();
            }
          })
          .catch((error) => {
            reject("Cannot validate spacer manifest: " + JSON.stringify(error));
          });
      }),
    [data, myFetch, setData, setEditedData],
  );

  const handleManifestSelected = useCallback(
    (e) => {
      e.preventDefault();
      let reader = new FileReader();
      let file = e.target.files[0];
      reader.onloadend = () => {
        let manifestStr = String(reader.result);
        // replace any CRLF characters in the file with just LF
        manifestStr = manifestStr.replace(/\r\n/g, "\n");
        setData((v) => {
          return { ...v, manifest: manifestStr };
        });
        setEditedData((v) => {
          return { ...v, manifest: manifestStr };
        });
      };
      reader.readAsText(file);
    },
    [setData, setEditedData],
  );

  const handleSpatialPanelSelected = useCallback(
    (e) => {
      e.preventDefault();
      let reader = new FileReader();
      let file = e.target.files[0];
      reader.onloadend = () => {
        let panelStr = String(reader.result);
        // replace CRLF characters in the file with just LF
        panelStr = panelStr.replace(/\r\n/g, "\n");
        setData((v) => {
          return { ...v, spatialPanel: panelStr };
        });
        setEditedData((v) => {
          return { ...v, spatialPanel: panelStr };
        });
      };
      reader.readAsText(file);
    },
    [setData, setEditedData],
  );

  const handleVcfFileSelected = useCallback(
    (e) => {
      e.preventDefault();
      let file = e.target.files[0];
      if (!file) return;
      let name = file.name.toLowerCase();
      let isGz = name.endsWith(".vcf.gz");
      if (!isGz && !name.endsWith(".vcf")) {
        notify("Please select a .vcf or .vcf.gz file", "error", 3000);
        e.target.value = "";
        return;
      }

      let applyVcfStr = (vcfStr: string) => {
        vcfStr = vcfStr.replace(/\r\n/g, "\n");
        setData((v) => ({ ...v, spatialVcf: vcfStr }));
        setEditedData((v) => ({ ...v, spatialVcf: vcfStr }));
      };

      if (isGz) {
        if (typeof DecompressionStream === "undefined") {
          notify(
            "This browser can't open .vcf.gz files -- please use a modern Chrome/Firefox/Safari, or upload a plain .vcf file",
            "error",
            5000,
          );
          e.target.value = "";
          return;
        }
        (async () => {
          try {
            let decompressed = new Response(
              file.stream().pipeThrough(new DecompressionStream("gzip")),
            );
            let vcfStr = await decompressed.text();
            applyVcfStr(vcfStr);
          } catch (err) {
            notify(
              "Failed to decompress .vcf.gz file -- is it a valid gzip file?",
              "error",
              3000,
            );
          }
        })();
        return;
      }

      let reader = new FileReader();
      reader.onloadend = () => {
        applyVcfStr(String(reader.result));
      };
      reader.readAsText(file);
    },
    [setData, setEditedData],
  );

  const handleSpacerManifestSelected = useCallback(
    (e) => {
      e.preventDefault();
      let reader = new FileReader();
      let file = e.target.files[0];
      reader.onloadend = () => {
        let spacerManifestStr = String(reader.result);
        // replace any CRLF characters in the file with just LF
        spacerManifestStr = spacerManifestStr.replace(/\r\n/g, "\n");
        setData((v) => {
          return { ...v, spacerManifestCsv: spacerManifestStr };
        });
        setEditedData((v) => {
          return { ...v, spacerManifestCsv: spacerManifestStr };
        });
      };
      reader.readAsText(file);
    },
    [setData, setEditedData],
  );

  return (
    <>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingBottom: "20px",
          paddingTop: "20px",
        }}
      >
        <Tabs
          items={
            isTissueKit
              ? [
                  "Cyto Parameters",
                  "Panel",
                  "VCF",
                  "Manifest",
                  "Spacer Manifest",
                  "Analysis Parameters",
                ]
              : [
                  "Cyto Parameters",
                  "Panel",
                  "Manifest",
                  "Spacer Manifest",
                  "Analysis Parameters",
                ]
          }
          style={{ fontSize: "12px", width: "800px" }}
          selectedItem={tab}
          onSelectedItemChange={(e) => {
            setTab(e);
          }}
        />
      </div>
      {/* Cyto Parameters */}
      <div
        style={
          isNarrow
            ? {
                display: tab === "Cyto Parameters" ? "flex" : "none",
                flexDirection: "column",
                gap: "2px",
                height: "calc(100% - 150px)",
                overflow: "auto",
                paddingBottom: "20px",
                backgroundColor: "#fBfBfB",
              }
            : {
                display: tab === "Cyto Parameters" ? "grid" : "none",
                gridAutoFlow: "column",
                gridTemplateRows: "repeat(auto-fill, minmax(20px, 2fr))",
                gap: "8px",
                height: "calc(100% - 150px)",
                overflowY: "auto",
                paddingBottom: "20px",
                backgroundColor: "#fBfBfB",
              }
        }
      >
        <div style={rowStyle}>
          <div style={labelStyle}>Group ID *</div>
          <SelectBox
            displayExpr="name"
            style={itemStyle}
            searchMode="startswith"
            searchExpr="name"
            searchEnabled
            showClearButton
            valueExpr="id"
            items={lookups.groups}
            readOnly={nameIsReadOnly}
            value={data.groupID}
            onValueChanged={(e) => {
              const groupID = e.value;
              if (groupID === 0) {
                setData((d) => {
                  return { ...d, groupID, runCountNumber: 0, runName: "" };
                });
                setEditedData((d) => {
                  return { ...d, groupID, runCountNumber: 0, runName: "" };
                });
              } else {
                const grp = lookups.groups.find((v) => v.id === e.value);
                if (grp == null) return;
                const rname =
                  grp.abbr +
                  "-" +
                  (grp.runCount + 1).toString().padStart(4, "0");
                setData((d) => {
                  return {
                    ...d,
                    groupID,
                    runCountNumber: grp.runCount + 1,
                    runName: rname,
                  };
                });
                setEditedData((d) => {
                  return {
                    ...d,
                    groupID,
                    runCountNumber: grp.runCount + 1,
                    runName: rname,
                  };
                });
              }
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Run Name *</div>
          <TextBox
            style={itemStyle}
            value={data?.runName}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, runName: e.value };
              });
              setEditedData((v) => {
                return { ...v, runName: e.value };
              });
            }}
            readOnly={data && data.groupID !== 0}
          >
            <Validator>
              <RequiredRule message="Run Name is required" />{" "}
            </Validator>
          </TextBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Run Length *</div>
          <SelectBox
            style={itemStyle}
            items={runLengths}
            displayExpr="name"
            valueExpr={"value"}
            value={data?.runLength}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, runLength: e.value };
              });
              setEditedData((v) => {
                return { ...v, runLength: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Recipe Length is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Requester *</div>
          <SelectBox
            style={itemStyle}
            items={lookups.emails}
            searchMode="startswith"
            searchEnabled
            showClearButton
            value={data?.requester}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, requester: e.value, operator: e.value };
              });
              setEditedData((v) => {
                return { ...v, requester: e.value, operator: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Requester is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Project *</div>
          <SelectBox
            style={itemStyle}
            items={lookups.projects}
            searchMode="startswith"
            searchEnabled
            showClearButton
            value={data?.project}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, project: e.value };
              });
              setEditedData((v) => {
                return { ...v, project: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Project is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Tile Selection Module *</div>
          <SelectBox
            style={itemStyle}
            items={lookups.tileSelectionModules}
            displayExpr="name"
            valueExpr="value"
            searchMode="startswith"
            acceptCustomValue
            onCustomItemCreating={(e) => {
              const newitem = {
                name: e.text,
                value: `tile_selections/${e.text}.txt`,
              };
              console.log("new item", newitem);
              lookups.tileSelectionModules.push(newitem);
              e.customItem = newitem;
            }}
            searchEnabled
            showClearButton
            value={data?.tileSelectionModule}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, tileSelectionModule: e.value };
              });
              setEditedData((v) => {
                return { ...v, tileSelectionModule: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Tile selection module is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Recipe Branch</div>

          <Autocomplete
            dataSource={recipeBranchStore}
            style={itemStyle}
            value={data.recipeBranch}
            valueExpr={"name"}
            onValueChanged={(e) => {
              console.log("newvalue", e);
              setData((v) => {
                return { ...v, recipeBranch: e.value };
              });
              setEditedData((v) => {
                return { ...v, recipeBranch: e.value };
              });
            }}
            minSearchLength={2}
            searchTimeout={500}
            placeholder="Type branch name..."
          >
            <Validator>
              <CustomRule
                message="The recipe branch must match an existing recipe branch"
                validationCallback={(e) => {
                  e.isValid = true;
                  console.log("validation callback", e);
                  if (!e.value) return true;
                  const found = (window as any).recipeBranches.find(
                    (v) => v.name === e.value,
                  );
                  if (found == null) {
                    e.isValid = false;
                    return false;
                  }
                  return true;
                }}
              />
            </Validator>
          </Autocomplete>
        </div>

        {/* <div style={rowStyle}>
          <div style={labelStyle} />
          <div style={{ ...itemStyle, display: "flex", gap: "5px" }}>
            <button
              style={{ marginLeft: "auto" }}
              onClick={() => setForceRefresh(true)}
            >
              Refresh Recipes
            </button>
          </div>
        </div> */}

        <div style={rowStyle}>
          <div style={labelStyle}>Tags</div>
          <TagBox
            style={itemStyle}
            value={data?.tags}
            width={"100%"}
            items={lookups.tags}
            searchEnabled
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, tags: e.value };
              });
              setEditedData((v) => {
                return { ...v, tags: e.value };
              });
              // theForm.current.instance.validate();
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Description</div>
          <TextBox
            style={itemStyle}
            value={data?.description}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, description: e.value };
              });
              setEditedData((v) => {
                return { ...v, description: e.value };
              });
              // theForm.current.instance.validate();
            }}
          >
            <Validator>
              <StringLengthRule
                max={100}
                message="Description must be less than 100 characters"
              />
              <PatternRule
                pattern="^[A-Za-z0-9\-_. ]+$"
                message="Only alphanumeric characters, as well as spaces, periods and hyphens are allowed in the description"
              />
            </Validator>
          </TextBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Validate Spatial Panel</div>
          <CheckBox
            style={itemStyle}
            value={data.validateSpatialPanel ?? true}
            onValueChanged={(e) => {
              setData((v) => ({ ...v, validateSpatialPanel: e.value }));
              setEditedData((v) => ({ ...v, validateSpatialPanel: e.value }));
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Cyto Small Cell</div>
          <CheckBox
            style={itemStyle}
            value={data.cytoSmallCell}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, cytoSmallCell: e.value };
              });
              setEditedData((v) => {
                return { ...v, cytoSmallCell: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Expanded Z</div>
          <CheckBox
            style={itemStyle}
            value={data.expandedZ}
            onValueChanged={(e) => {
              if (e.value) {
                setEnableEditingZSettings(false);
              }
              const update = {
                expandedZ: e.value,
                ...spatialZDefaults(data.spatialKitType),
              };
              setData((v) => {
                return { ...v, ...update };
              });
              setEditedData((v) => {
                return { ...v, ...update };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Illumination Correction</div>
          <CheckBox
            style={itemStyle}
            value={data.illuminationCorrection}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, illuminationCorrection: e.value };
              });
              setEditedData((v) => {
                return { ...v, illuminationCorrection: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Save Per Target Background Images</div>
          <CheckBox
            style={itemStyle}
            value={data.savePerTargetBkgImages}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, savePerTargetBkgImages: e.value };
              });
              setEditedData((v) => {
                return { ...v, savePerTargetBkgImages: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Save Raw Projection Images</div>
          <CheckBox
            style={itemStyle}
            value={data.saveRawProjections}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, saveRawProjections: e.value };
              });
              setEditedData((v) => {
                return { ...v, saveRawProjections: e.value };
              });
            }}
          />
        </div>

        {/* Tissue-only parameters */}
        {isTissueKit && (
          <>
            <div style={rowStyle}>
              <div style={labelStyle}>Save Flow Cell for Post-Run H&E</div>
              <CheckBox
                style={itemStyle}
                value={data.saveFlowCellForPostRunHE}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, saveFlowCellForPostRunHE: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, saveFlowCellForPostRunHE: e.value };
                  });
                }}
              />
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>Tile Overlap X</div>
              <NumberBox
                style={itemStyle}
                value={data?.tileOverlapX}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, tileOverlapX: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, tileOverlapX: e.value };
                  });
                }}
              >
                <Validator>
                  <RangeRule
                    min={0}
                    max={1}
                    message="Tile Overlap X must be between 0 and 1"
                  />
                </Validator>
              </NumberBox>
            </div>

            <div style={rowStyle}>
              <div style={labelStyle}>Tile Overlap Y</div>
              <NumberBox
                style={itemStyle}
                value={data?.tileOverlapY}
                onValueChanged={(e) => {
                  setData((v) => {
                    return { ...v, tileOverlapY: e.value };
                  });
                  setEditedData((v) => {
                    return { ...v, tileOverlapY: e.value };
                  });
                }}
              >
                <Validator>
                  <RangeRule
                    min={0}
                    max={1}
                    message="Tile Overlap Y must be between 0 and 1"
                  />
                </Validator>
              </NumberBox>
            </div>
          </>
        )}

        <div style={rowStyle}>
          <div style={labelStyle}>Enable Editing Z-Settings</div>
          <CheckBox
            style={itemStyle}
            value={enableEditingZSettings}
            disabled={data.expandedZ}
            onValueChanged={(e) => {
              setEnableEditingZSettings(e.value);
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Num Z Slices</div>
          <NumberBox
            style={itemStyle}
            disabled={!enableEditingZSettings}
            value={data?.numSpatialZSlices}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, numSpatialZSlices: e.value };
              });
              setEditedData((v) => {
                return { ...v, numSpatialZSlices: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Z Offset</div>
          <NumberBox
            style={itemStyle}
            disabled={!enableEditingZSettings}
            value={data?.zSliceOffset}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, zSliceOffset: e.value };
              });
              setEditedData((v) => {
                return { ...v, zSliceOffset: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Z Slice Pitch</div>
          <NumberBox
            style={itemStyle}
            disabled={!enableEditingZSettings}
            value={data?.zSlicePitch}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, zSlicePitch: e.value };
              });
              setEditedData((v) => {
                return { ...v, zSlicePitch: e.value };
              });
            }}
          />
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Well Layout</div>
          <SelectBox
            style={itemStyle}
            items={isTissueKit ? tissueWellLayoutOptions : wellLayoutOptions}
            displayExpr="name"
            valueExpr="value"
            value={data.wellLayout}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, wellLayout: e.value };
              });
              setEditedData((v) => {
                return { ...v, wellLayout: e.value };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Well Layout is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        <div style={rowStyle}>
          <div style={labelStyle}>Spatial Kit Type</div>
          <SelectBox
            style={itemStyle}
            items={spatialKitTypes}
            value={data.spatialKitType}
            onValueChanged={(e) => {
              const spatialKitTypeEnum =
                spatialKitTypeMap[e.value || "KitTypeCytoprofiling"];
              setFilteredCartridges(
                cartridges.filter((v) =>
                  v.SpatialKitType.includes(spatialKitTypeEnum),
                ),
              );
              // Protein-only kits must not carry a DISS cartridge PN (backend rejects it).
              const clearCartridgePn =
                e.value === "KitTypeProteinScreening"
                  ? { spatialCartridgePartNumber: "" }
                  : {};
              if (e.value !== "KitTypeTissue" && tab === "VCF") {
                setTab("Cyto Parameters");
              }
              const allowedWellLayouts =
                e.value === "KitTypeTissue"
                  ? tissueWellLayoutOptions
                  : wellLayoutOptions;
              const resetWellLayout = allowedWellLayouts.some(
                (o) => o.value === data.wellLayout,
              )
                ? {}
                : {
                    wellLayout:
                      e.value === "KitTypeTissue"
                        ? defaultTissueWellLayout
                        : "",
                  };
              setData((v) => {
                return {
                  ...v,
                  spatialKitType: e.value,
                  ...clearCartridgePn,
                  ...resetWellLayout,
                  ...spatialZDefaults(e.value),
                };
              });
              setEditedData((v) => {
                return {
                  ...v,
                  spatialKitType: e.value,
                  ...clearCartridgePn,
                  ...resetWellLayout,
                  ...spatialZDefaults(e.value),
                };
              });
            }}
          >
            <Validator>
              <RequiredRule message="Spatial Kit Type is required" />{" "}
            </Validator>
          </SelectBox>
        </div>

        {data.spatialKitType !== "KitTypeProteinScreening" && (
          <div style={rowStyle}>
            <div style={labelStyle}>Spatial Cartridge Part Number</div>
            <SelectBox
              style={itemStyle}
              displayExpr="Name"
              valueExpr="PartNumber"
              items={filteredCartridges}
              value={data.spatialCartridgePartNumber}
              onValueChanged={(e) => {
                console.log("set spatialCartridgePartNumber", e.value);
                setData((v) => {
                  return { ...v, spatialCartridgePartNumber: e.value };
                });
                setEditedData((v) => {
                  return { ...v, spatialCartridgePartNumber: e.value };
                });
              }}
            >
              <Validator>
                <RequiredRule message="Spatial Cartridge Part Number is required" />{" "}
              </Validator>
            </SelectBox>
          </div>
        )}

        <div style={rowStyle}>
          <div style={labelStyle}>Spatial Protein Cartridge Part Number</div>
          <SelectBox
            style={itemStyle}
            displayExpr="Name"
            valueExpr="PartNumber"
            items={proteinCartridgeOptions}
            showClearButton
            value={data.spatialProteinPartNumber}
            onValueChanged={(e) => {
              // showClearButton emits null/undefined on clear; normalize to "" so the PATCH
              // sends an explicit removal instead of omitting the field (SW-28441).
              const value = e.value ?? "";
              console.log("set spatialProteinPartNumber", value);
              setData((v) => {
                return { ...v, spatialProteinPartNumber: value };
              });
              setEditedData((v) => {
                return { ...v, spatialProteinPartNumber: value };
              });
            }}
          />
        </div>

        {/* End */}
      </div>

      {/* Panel */}
      <div
        style={{
          display: tab === "Panel" ? "flex" : "none",
          flexDirection: "column",
          gap: "2px",
          height: "calc(100% - 150px)",
          overflow: "auto",
          paddingBottom: "20px",
          backgroundColor: "#fBfBfB",
        }}
      >
        <div style={{ ...rowStyle, marginBottom: "10px" }}>
          <div style={labelStyle}>Panel Type</div>
          <SelectBox
            style={itemStyle}
            items={["CSV", "JSON"]}
            value={data.spatialPanelType}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, spatialPanelType: e.value };
              });
              setEditedData((v) => {
                return { ...v, spatialPanelType: e.value };
              });
            }}
          ></SelectBox>
        </div>

        <TextArea
          height="90%"
          width="100%"
          spellcheck={false}
          value={data.spatialPanel}
          onValueChanged={(e) => {
            setData((v) => {
              return { ...v, spatialPanel: e.value };
            });
            setEditedData((v) => {
              return { ...v, spatialPanel: e.value };
            });
          }}
        >
          <Validator>
            <AsyncRule validationCallback={validatePanel} />
          </Validator>
        </TextArea>
        <span
          style={{
            fontSize: "12px",
            color: "#909090",
            marginTop: "20px",
          }}
        >
          {"Spatial Panel" +
            (spatialPanelParseMessage ? ": " + spatialPanelParseMessage : "")}
        </span>
        <div>
          <label
            htmlFor="file-upload-panel"
            style={{
              border: "1px solid #ccc",
              display: "inline-block",
              padding: "6px 12px",
              cursor: "pointer",
              marginTop: "10px",
            }}
          >
            Load Panel...
          </label>
          <input
            id="file-upload-panel"
            type="file"
            style={{ display: "none" }}
            onChange={handleSpatialPanelSelected}
          />
        </div>
      </div>

      {/* VCF */}
      {isTissueKit && (
        <div
          style={{
            display: tab === "VCF" ? "flex" : "none",
            flexDirection: "column",
            gap: "2px",
            height: "calc(100% - 150px)",
            overflow: "auto",
            paddingBottom: "20px",
            backgroundColor: "#fBfBfB",
          }}
        >
          <TextArea
            height="90%"
            width="100%"
            spellcheck={false}
            value={data.spatialVcf}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, spatialVcf: e.value };
              });
              setEditedData((v) => {
                return { ...v, spatialVcf: e.value };
              });
            }}
          />
          <div>
            <label
              htmlFor="file-upload-vcf"
              style={{
                border: "1px solid #ccc",
                display: "inline-block",
                padding: "6px 12px",
                cursor: "pointer",
                marginTop: "10px",
              }}
            >
              Load VCF...
            </label>
            <input
              id="file-upload-vcf"
              type="file"
              accept=".vcf,.gz"
              style={{ display: "none" }}
              onChange={handleVcfFileSelected}
            />
          </div>
        </div>
      )}

      {/* Manifest */}
      <div
        style={{
          display: tab === "Manifest" ? "flex" : "none",
          flexDirection: "column",
          gap: "2px",
          height: "calc(100% - 150px)",
          overflow: "auto",
          paddingBottom: "20px",
          backgroundColor: "#fBfBfB",
        }}
      >
        <TextArea
          height="90%"
          width="100%"
          spellcheck={false}
          value={data.manifest}
          onValueChanged={(e) => {
            setData((v) => {
              return { ...v, manifest: e.value };
            });
            setEditedData((v) => {
              return { ...v, manifest: e.value };
            });
          }}
        >
          <Validator ref={manifestValidatorRef}>
            <AsyncRule validationCallback={validateManifest} />
          </Validator>
        </TextArea>
        <div>
          <label
            htmlFor="file-upload-manifest"
            style={{
              border: "1px solid #ccc",
              display: "inline-block",
              padding: "6px 12px",
              cursor: "pointer",
              marginTop: "20px",
            }}
          >
            Load Manifest...
          </label>
          <input
            id="file-upload-manifest"
            type="file"
            style={{ display: "none" }}
            onChange={handleManifestSelected}
          />
        </div>
      </div>

      {/* Spacer Manifest*/}
      <div
        style={{
          display: tab === "Spacer Manifest" ? "flex" : "none",
          flexDirection: "column",
          gap: "2px",
          height: "calc(100% - 150px)",
          overflow: "auto",
          paddingBottom: "20px",
          backgroundColor: "#fBfBfB",
        }}
      >
        <TextArea
          height="90%"
          width="100%"
          spellcheck={false}
          value={data.spacerManifestCsv}
          onValueChanged={(e) => {
            setData((v) => {
              return { ...v, spacerManifestCsv: e.value };
            });
            setEditedData((v) => {
              return { ...v, spacerManifestCsv: e.value };
            });
          }}
        >
          <Validator>
            <AsyncRule validationCallback={validateSpacerManifest} />
          </Validator>
        </TextArea>
        <span
          style={{
            fontSize: "12px",
            color: "#909090",
            marginTop: "20px",
          }}
        >
          {"Spatial Panel" +
            (spacerManifestParseMessage
              ? ": " + spacerManifestParseMessage
              : "")}
        </span>
        <div>
          <label
            htmlFor="file-upload-spacer-manifest"
            style={{
              border: "1px solid #ccc",
              display: "inline-block",
              padding: "6px 12px",
              cursor: "pointer",
              marginTop: "20px",
            }}
          >
            Load Spacer Manifest...
          </label>
          <input
            id="file-upload-spacer-manifest"
            type="file"
            style={{ display: "none" }}
            onChange={handleSpacerManifestSelected}
          />
        </div>
      </div>

      {/* Analysis Parameters */}
      <div
        style={{
          display: tab === "Analysis Parameters" ? "flex" : "none",
          flexDirection: "column",
          gap: "20px",
          height: "calc(100% - 150px)",
          overflow: "auto",
          paddingBottom: "20px",
          backgroundColor: "#fBfBfB",
        }}
      >
        <div style={rowStyle}>
          <div style={labelStyleAnalysis}>Analysis Enabled</div>
          <CheckBox
            style={itemStyle}
            value={data.analysisEnabled}
            onValueChanged={(e) => {
              setData((v) => {
                return { ...v, analysisEnabled: e.value };
              });
              setEditedData((v) => {
                return { ...v, analysisEnabled: e.value };
              });
            }}
          />
        </div>

        {data.analysisEnabled && (
          <>
            <div style={rowStyle}>
              <div style={labelStyleAnalysis}>
                Cloud Analysis Only (no OBPA)
              </div>
              <CheckBox
                style={itemStyle}
                value={data.cloudAnalysis}
                onValueChanged={(e) => {
                  const obpaROI = !e.value
                    ? { roiWidth: newRun.roiWidth, roiHeight: newRun.roiHeight }
                    : {};
                  setData((v) => {
                    return { ...v, cloudAnalysis: e.value, ...obpaROI };
                  });
                  setEditedData((v) => {
                    return { ...v, cloudAnalysis: e.value, ...obpaROI };
                  });
                }}
              />
            </div>

            {data.cloudAnalysis ? (
              <>
                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Analysis Tool Rep</div>
                  <TextBox
                    style={itemStyle}
                    value={data?.analysisToolRep}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, analysisToolRep: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, analysisToolRep: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Image Name</div>
                  <TextBox
                    style={itemStyle}
                    value={data?.imageName}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, imageName: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, imageName: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Arguments</div>
                  <TextBox
                    style={itemStyle}
                    value={data?.arguments}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, arguments: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, arguments: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>ROI Width</div>
                  <NumberBox
                    style={itemStyle}
                    value={data?.roiWidth}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, roiWidth: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, roiWidth: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>ROI Height</div>
                  <NumberBox
                    style={itemStyle}
                    value={data?.roiHeight}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, roiHeight: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, roiHeight: e.value };
                      });
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Log Level</div>
                  <SelectBox
                    style={itemStyle}
                    items={logLevels}
                    value={data.logLevel}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, logLevel: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, logLevel: e.value };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Save Intensities</div>
                  <SelectBox
                    style={itemStyle}
                    items={saveIntensitiesOptions}
                    value={data.saveIntensities}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return {
                          ...v,
                          saveIntensities: e.value,
                          uploadIntensities:
                            e.value === "some" || e.value === "all",
                        };
                      });
                      setEditedData((v) => {
                        return {
                          ...v,
                          saveIntensities: e.value,
                          uploadIntensities:
                            e.value === "some" || e.value === "all",
                        };
                      });
                    }}
                  />
                </div>

                <div style={rowStyle}>
                  <div style={labelStyleAnalysis}>Zip CNN Images</div>
                  <CheckBox
                    style={itemStyle}
                    value={data.zipCNNImages}
                    onValueChanged={(e) => {
                      setData((v) => {
                        return { ...v, zipCNNImages: e.value };
                      });
                      setEditedData((v) => {
                        return { ...v, zipCNNImages: e.value };
                      });
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default AddRunSpatialContent;
